import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import api from '../components/api';
import { 
  NotesHeader, 
  NotesTable, 
  NotesStats, 
  NotesCharts,
  useNotesCalculations
} from '../components/notes';

const ListeNotePage = () => {
  const { controleId } = useParams();
  
  const [controle, setControle] = useState(null);
  const [allNotes, setAllNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    loadNotesData();
  }, [controleId]);

  const loadNotesData = async () => {
    try {
      setLoading(true);
      
      // Un seul appel pour charger contrôle et toutes les notes
      const [controleResponse, notesResponse] = await Promise.all([
        api.get(`/controle/controles/${controleId}/`),
        api.get(`/controle/controles/${controleId}/notes/`)
      ]);
      
      setControle(controleResponse.data);
      setAllNotes(notesResponse.data);
      
    } catch (error) {
      console.error('❌ Erreur lors du chargement:', error);
      toast.error(error.response?.status === 404 ? 'Évaluation introuvable' : 'Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  // Filtrage et pagination côté client
  const filteredNotes = useMemo(() => {
    if (!searchTerm) return allNotes;
    
    return allNotes.filter(note => 
      note.etudiant.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.etudiant.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.etudiant.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.note.toString().includes(searchTerm) ||
      note.mention.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (note.observation && note.observation.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [allNotes, searchTerm]);

  // Pagination côté client
  const paginatedNotes = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredNotes.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredNotes, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredNotes.length / itemsPerPage);
  
  // Utiliser le hook personnalisé pour les calculs
  const { stats } = useNotesCalculations(allNotes);

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
            <span className="ml-3 text-gray-600">Chargement des notes...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <NotesHeader controle={controle} />
        
        <NotesTable 
          paginatedNotes={paginatedNotes}
          filteredNotes={filteredNotes}
          allNotes={allNotes}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          controle={controle}
        />
        
        <NotesStats stats={stats} />
        
        <NotesCharts stats={stats} />
      </div>
    </Layout>
  );
};

export default ListeNotePage;
